import './src/styles/global.css'
import './src/styles/webfonts.css'
import './src/styles/plyr.css'

import { ScrollTrigger } from 'gsap/ScrollTrigger'

/**
 * Anchor / hash scrolling.
 *
 * The report is one long page whose layout is NOT final at first paint:
 * gatsby-plugin-image lazy images and (most importantly) the pinned GSAP
 * ScrollTriggers only create their pin-spacers on `ScrollTrigger.refresh()`,
 * which is intentionally deferred to `window.load + 300ms`
 * (see ContentStage2025.js). Those pin-spacers inject a lot of document
 * height, so a native/early jump to `#Jubilaeum-25` parks the scroll at a
 * pixel offset that becomes wrong the moment the pins expand the page.
 *
 * So we take over: tell Gatsby NOT to manage scroll when there is a hash,
 * then scroll there ourselves once the layout has settled, re-asserting on
 * every ScrollTrigger.refresh() (and a few timed fallbacks) until the page
 * stops moving — using the exact offset math from navigation.js so the
 * landing spot matches a normal nav click.
 */

// Don't let Gatsby's scroll updater fight us when a hash is present.
export const shouldUpdateScroll = ({ routerProps: { location } }) => {
  if (location && location.hash) {
    return false
  }
  return true
}

// Mirror the offset logic in src/components/layout/navigation.js so a deep
// link lands exactly where clicking the nav would.
const computeTopOffset = (el) => {
  const isSpecialContent = el.classList.contains('vorwort')
  const isMobile = window.innerWidth < 1024
  const nav = document.querySelector('.sptg__navigation')
  const navHeight = nav ? nav.offsetHeight : 0
  const vhToPixels = (vh) => window.innerHeight * (vh / 100)

  const offset = isSpecialContent ? 160 : isMobile ? navHeight : vhToPixels(-140)
  return el.getBoundingClientRect().top + window.pageYOffset - offset
}

const scrollToHash = (rawHash) => {
  if (!rawHash) return

  // hash includes the leading '#'; decode in case of umlauts etc.
  const id = decodeURIComponent(rawHash.replace(/^#/, ''))
  if (!id) return

  let cancelled = false
  const stop = () => {
    cancelled = true
    window.removeEventListener('wheel', onUserScroll, { passive: true })
    window.removeEventListener('touchstart', onUserScroll, { passive: true })
    window.removeEventListener('keydown', onUserScroll)
    ScrollTrigger.removeEventListener('refresh', jump)
  }

  // If the visitor takes over, get out of the way immediately.
  const onUserScroll = () => stop()

  const jump = () => {
    if (cancelled) return
    const el = document.getElementById(id)
    if (!el) return
    window.scrollTo({ top: computeTopOffset(el), behavior: 'auto' })
  }

  window.addEventListener('wheel', onUserScroll, { passive: true })
  window.addEventListener('touchstart', onUserScroll, { passive: true })
  window.addEventListener('keydown', onUserScroll)

  // Re-assert the target every time GSAP re-measures (pin-spacers appear,
  // images finish loading, etc.). This is what catches the big height jump.
  ScrollTrigger.addEventListener('refresh', jump)

  const begin = () => {
    if (cancelled) return
    jump()
    // Timed fallbacks in case a refresh fires before this handler is
    // attached, or there are no ScrollTriggers on this section at all.
    const delays = [50, 350, 700, 1200, 2000]
    delays.forEach((d) => setTimeout(jump, d))
    // Stop hijacking after the page has had time to settle.
    setTimeout(stop, 2600)
  }

  if (document.readyState === 'complete') {
    begin()
  } else {
    window.addEventListener('load', begin, { once: true })
  }
}

// Fires once after the first client-side render of a fresh tab.
export const onInitialClientRender = () => {
  if (typeof window === 'undefined') return
  scrollToHash(window.location.hash)
}
